/**
 * Home — the Orb, the day, and nothing else (REDESIGN.md §5).
 *
 * This screen does not scroll. Everything on it has to fit one viewport,
 * including an iPhone SE, which is why the Orb zone takes the slack with flex
 * rather than a fixed height. It carries no cards and no status text: whatever
 * needs saying is said by the Orb.
 *
 * Home has carried its navigation three ways now — four large circles, then the
 * shared bottom bar, then the circles again. It carries none of them any more:
 * the header is the same header every screen has, orb and all, and it is drawn
 * by the shell rather than here.
 */

import { mountOrb } from '../components/orb.js'
import { apiPost } from '../js/api.js'
import { text } from '../js/html.js'
import { parseUtc } from '../js/dates.js'
import { canPromptInstall, isIOS, isStandalone, promptInstall } from '../js/platform.js'

/**
 * The nudge to install, and whether it has been waved away.
 *
 * Onboarding offers installation once; somebody who skipped it then has no way
 * back to it, and the app spends its life inside browser chrome it was not
 * designed for. So Home asks again — quietly, once, and never after it has been
 * dismissed. The document gives Home only the Practical link at its foot (§5);
 * this is one line more, and it disappears the moment it is answered.
 */
const INSTALL_KEY = 'differlx:install-dismissed'

function installDismissed() {
  try {
    return window.localStorage.getItem(INSTALL_KEY) === '1'
  } catch {
    return false
  }
}

function dismissInstall() {
  try {
    window.localStorage.setItem(INSTALL_KEY, '1')
  } catch {
    // Nothing is lost that matters; the hint simply returns next time.
  }
}

/** Worth asking only when it is not already installed, and not already refused. */
function shouldOfferInstall() {
  return !isStandalone() && !installDismissed()
}

/**
 * What this device has already been shown.
 *
 * Server-side read receipts would be the stronger answer, but they are not in
 * this step and inventing one would mean inventing data. This is honest about
 * what it is: a note on this device about the newest brief it has seen. Storage
 * can refuse (private windows, cleared data), so every access is guarded and a
 * failure simply means nothing is flagged.
 */
const SEEN_KEY = 'differlx:seen-path'

function lastSeen() {
  try {
    return window.localStorage.getItem(SEEN_KEY)
  } catch {
    return null
  }
}

export function markPathSeen(timeline = []) {
  const newest = newestPublished(timeline)

  if (newest === null) return

  try {
    window.localStorage.setItem(SEEN_KEY, newest)
  } catch {
    // Nothing is lost that matters; the circle simply keeps its halo.
  }
}

function newestPublished(timeline) {
  const open = timeline.filter((entry) => !entry.locked && entry.publish_at)

  return open.length === 0 ? null : open[open.length - 1].publish_at
}

/** True when something has arrived on Path that this device has not opened. */
function pathHasNews(timeline) {
  const newest = newestPublished(timeline)

  if (newest === null) return false

  const seen = lastSeen()

  return seen === null || newest > seen
}

export function renderHome({ timeline = [] }) {
  const progress = dayProgress(timeline)

  return `
    <div class="home">
      ${backdrop(timeline)}

      <div class="home__orb" data-orb-stage></div>

      ${progress === null ? '<div class="home__day"></div>' : dayIndicator(progress, timeline)}

      <p class="home__practical">
        <a href="/prakticno" data-link>Practical info</a>
      </p>

      ${shouldOfferInstall() ? installHint() : ''}
    </div>
  `
}

/**
 * One line, and the way to act on it. Chrome can install from a button; iOS
 * cannot be prompted at all, so there it is instructions or nothing.
 */
function installHint() {
  return `
    <p class="home__install" data-install-hint>
      <span>
        ${
          isIOS()
            ? 'Add this to your home screen and it opens full screen, without the browser.'
            : 'Install it and it opens full screen, without the browser.'
        }
      </span>
      ${canPromptInstall() ? '<button data-install>Install</button>' : ''}
      <button data-install-dismiss aria-label="Dismiss">×</button>
    </p>
  `
}

/**
 * The place, faintly, behind everything.
 *
 * The picture from the most recent open brief, washed out and faded into the
 * page. It is there to make Home feel like somewhere rather than a form — so it
 * is quiet enough that the Orb stays the brightest thing on the screen, and it
 * simply does not appear when no brief has a picture. A grey rectangle would be
 * worse than nothing.
 */
function backdrop(timeline) {
  const withPicture = timeline.filter((entry) => !entry.locked && entry.image_url)
  const latest = withPicture.at(-1)

  if (latest === undefined) {
    return ''
  }

  return `<span class="home__backdrop" aria-hidden="true"
                style="background-image:url('${text(latest.image_url)}')"></span>`
}

/** Which sections have something this device has not opened yet. */
export function alertsFor(timeline = []) {
  return pathHasNews(timeline) ? ['path'] : []
}

function dayIndicator({ day, total, percent }, timeline) {
  const act = currentAct(timeline)

  return `
    <div class="home__day">
      <p class="home__day-label">
        DAY <b>${day}</b> / ${total}${act ? ` · ${text(act.toUpperCase())}` : ''}
      </p>
      <span class="home__track"><span style="width:${percent}%"></span></span>
    </div>
  `
}

/** The act of the most recent published brief — the chapter you are in. */
function currentAct(timeline) {
  const open = timeline.filter((entry) => !entry.locked)

  return open.at(-1)?.act ?? null
}

/**
 * Where you are in the whole programme, measured between the first and last
 * dated point rather than the week on the island.
 */
function dayProgress(timeline) {
  const dated = timeline.filter((entry) => entry.publish_at)

  if (dated.length < 2) {
    return null
  }

  const first = parseUtc(dated[0].publish_at).getTime()
  const last = parseUtc(dated.at(-1).publish_at).getTime()

  const total = Math.max(1, Math.round((last - first) / 86400000) + 1)
  const day = Math.min(total, Math.max(1, Math.round((Date.now() - first) / 86400000) + 1))

  return { day, total, percent: Math.round((day / total) * 1000) / 10 }
}

/**
 * Mounts the Orb once the screen is in the DOM. The Orb enters `signal` when
 * something unseen is waiting, which is the same condition that haloes the
 * Path circle — one fact, shown twice.
 */
export function mountHome(root, { user, retreat, timeline = [], orb: message = null, offline = false }) {
  const stage = root.querySelector('[data-orb-stage]')

  if (stage === null) return

  const news = pathHasNews(timeline)
  // A message nobody has met yet is delivered out loud, once.
  const deliver = message !== null && !message.seen

  const orb = mountOrb(stage, {
    firstName: (user?.name ?? '').split(/\s+/)[0] ?? '',
    state: deliver || news ? 'signal' : 'idle',
    // The Orb's own line outranks the situational one (REDESIGN.md §4).
    message: message?.message ?? null,
    audioUrl: message?.audio_url ?? null,
    deliver,
    onDelivered: () => {
      // It has been said; the server remembers so the next device does not
      // announce it again. A failure here only means it is said twice.
      apiPost(`/orb/${message.id}/seen`).catch(() => {})
    },
    context: situationFor(timeline),
    readout: { online: !offline, onSite: onSite(retreat), place: retreat?.place_noun },
  })

  mountInstallHint(root)

  return () => orb.destroy()
}

function mountInstallHint(root) {
  const hint = root.querySelector('[data-install-hint]')

  if (hint === null) return

  hint.querySelector('[data-install-dismiss]').addEventListener('click', () => {
    dismissInstall()
    hint.remove()
  })

  hint.querySelector('[data-install]')?.addEventListener('click', async () => {
    const outcome = await promptInstall()

    if (outcome === 'accepted') {
      dismissInstall()
      hint.remove()
    }
  })
}

/** The facts orbLines needs to choose its one sentence. */
function situationFor(timeline) {
  const progress = dayProgress(timeline)
  const next = timeline.find((entry) => entry.locked)

  const daysToNext =
    next === undefined
      ? null
      : Math.max(0, Math.ceil((parseUtc(next.publish_at).getTime() - Date.now()) / 86400000))

  return { day: progress?.day ?? null, total: progress?.total ?? null, daysToNext }
}

/** True while the retreat is actually happening, wherever it happens. */
function onSite(retreat) {
  if (!retreat?.starts_on || !retreat?.ends_on) return false

  const today = new Date().toISOString().slice(0, 10)

  return today >= retreat.starts_on && today <= retreat.ends_on
}
